import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col, Card, ButtonGroup, Button, Alert } from 'react-bootstrap';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import api from '../../../utils/api';

// Categorical palette (validated, fixed slot order). Color follows the metric everywhere.
const C = {
  sleep: '#2a78d6', // blue
  readiness: '#008300', // green
  steps: '#2a78d6', // blue (single-series chart)
  drinks: '#eb6834', // orange
  adherence: '#008300', // green
  weight: '#4a3aa7', // violet
  restingHR: '#e34948', // red
  grid: '#e6e5e1',
  text: '#52514e',
};

const axisProps = { tick: { fontSize: 11, fill: C.text }, stroke: C.grid };

// Monday of the week a YYYY-MM-DD date falls in.
const weekStart = (dateStr) => {
  const d = new Date(`${dateStr}T12:00:00`);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
};

const shortDate = (dateStr) => {
  const d = new Date(`${dateStr}T12:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const ChartCard = ({ title, children }) => (
  <Card className="mb-3">
    <Card.Header className="py-2">{title}</Card.Header>
    <Card.Body style={{ height: 240 }}>{children}</Card.Body>
  </Card>
);

const TrendsTab = () => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [trendsRes, settingsRes] = await Promise.all([
        api.get(`/health/trends?days=${days}`),
        api.get('/health/settings'),
      ]);
      setData(trendsRes.data);
      setSettings(settingsRes.data);
      setError(null);
    } catch (err) {
      setError('Failed to load trend data.');
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!data) return <p>Loading...</p>;

  const ouraSeries = data.oura.map((o) => ({ ...o, label: shortDate(o.date) }));

  const weightSeries = data.logs
    .filter((l) => l.weight != null)
    .map((l) => ({ date: l.date, label: shortDate(l.date), weight: l.weight }));

  // Weekly rollups: drinks per week + med adherence per week.
  const weeks = {};
  data.logs.forEach((l) => {
    const w = weekStart(l.date);
    weeks[w] = weeks[w] || { week: w, label: shortDate(w), drinks: 0, medsTaken: 0, medsTotal: 0 };
    weeks[w].drinks += (l.drinks || []).length;
    weeks[w].medsTaken += (l.meds || []).filter((m) => m.taken).length;
    weeks[w].medsTotal += (l.meds || []).length;
  });
  const weekly = Object.values(weeks)
    .sort((a, b) => (a.week < b.week ? -1 : 1))
    .map((w) => ({ ...w, adherence: w.medsTotal ? Math.round((w.medsTaken / w.medsTotal) * 100) : null }));

  const noOura = ouraSeries.length === 0;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <ButtonGroup size="sm">
          {[30, 90, 180].map((n) => (
            <Button key={n} variant={days === n ? 'primary' : 'outline-primary'} onClick={() => setDays(n)}>
              {n} days
            </Button>
          ))}
        </ButtonGroup>
        {noOura && <span className="text-muted small">No Oura data yet. Add your token in Settings and sync.</span>}
      </div>

      <Row>
        <Col lg={6}>
          <ChartCard title="Sleep & Readiness (Oura scores)">
            <ResponsiveContainer>
              <LineChart data={ouraSeries}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="label" {...axisProps} minTickGap={30} />
                <YAxis domain={[40, 100]} {...axisProps} width={30} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="sleepScore" name="Sleep" stroke={C.sleep} strokeWidth={2} dot={false} connectNulls />
                <Line type="monotone" dataKey="readinessScore" name="Readiness" stroke={C.readiness} strokeWidth={2} dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>

        <Col lg={6}>
          <ChartCard title="Steps per day">
            <ResponsiveContainer>
              <BarChart data={ouraSeries} barCategoryGap={2}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="label" {...axisProps} minTickGap={30} />
                <YAxis {...axisProps} width={40} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                <Tooltip />
                <Bar dataKey="steps" name="Steps" fill={C.steps} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>

        <Col lg={6}>
          <ChartCard title={`Drinks per week${settings?.weeklyDrinkTarget ? ` (target ${settings.weeklyDrinkTarget})` : ''}`}>
            <ResponsiveContainer>
              <BarChart data={weekly} barCategoryGap={4}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="label" {...axisProps} minTickGap={20} />
                <YAxis {...axisProps} width={30} allowDecimals={false} />
                <Tooltip />
                {settings?.weeklyDrinkTarget != null && (
                  <ReferenceLine y={settings.weeklyDrinkTarget} stroke={C.text} strokeDasharray="4 4" />
                )}
                <Bar dataKey="drinks" name="Drinks" fill={C.drinks} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>

        <Col lg={6}>
          <ChartCard title="Medication adherence per week (%)">
            <ResponsiveContainer>
              <BarChart data={weekly} barCategoryGap={4}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="label" {...axisProps} minTickGap={20} />
                <YAxis domain={[0, 100]} {...axisProps} width={30} />
                <Tooltip />
                <Bar dataKey="adherence" name="Adherence %" fill={C.adherence} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>

        <Col lg={6}>
          <ChartCard title="Weight (lbs)">
            <ResponsiveContainer>
              <LineChart data={weightSeries}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="label" {...axisProps} minTickGap={30} />
                <YAxis domain={['dataMin - 3', 'dataMax + 3']} {...axisProps} width={35} />
                <Tooltip />
                <Line type="monotone" dataKey="weight" name="Weight" stroke={C.weight} strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>

        <Col lg={6}>
          <ChartCard title="Resting heart rate (Oura)">
            <ResponsiveContainer>
              <LineChart data={ouraSeries}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="label" {...axisProps} minTickGap={30} />
                <YAxis domain={['dataMin - 3', 'dataMax + 3']} {...axisProps} width={30} />
                <Tooltip />
                <Line type="monotone" dataKey="restingHeartRate" name="Resting HR" stroke={C.restingHR} strokeWidth={2} dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>
      </Row>
    </div>
  );
};

export default TrendsTab;
